import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Building2, Plus, UserMinus, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Paginated, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDays, formatNumber } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Select,
  StatusDot,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';

export function DepartmentsPage() {
  const { t, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<Paginated<any>>('/departments', { pageSize: 100 }),
  });

  return (
    <>
      <PageHeader
        title={t('departments.title')}
        subtitle={can(P.USERS_VIEW_ALL) ? undefined : t('reach.departmentsSubtitle')}
        action={
          can(P.DEPARTMENTS_MANAGE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/departments/new')}>
              {t('departments.new')}
            </Button>
          )
        }
      />

      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.items.length ? (
        <Card><EmptyState icon={<Building2 className="h-8 w-8" />} /></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {data.items.map((d) => (
            <button
              key={d.id}
              onClick={() => navigate(`/departments/${d.id}`)}
              className="surface rounded-lg p-5 text-start transition-colors duration-fast hover:border-primary/50"
            >
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{d.code}</span>
                  <h3 className="truncate text-h3 text-[var(--text-primary)]">{localized(d)}</h3>
                </div>
                <StatusDot tone={d.isActive ? 'online' : 'offline'} />
              </div>
              {d.description && <p className="mb-3 line-clamp-2 text-body-sm text-muted">{d.description}</p>}
              <div className="flex flex-wrap gap-3 text-caption text-muted">
                <span>{d._count.members} {t('departments.members').toLowerCase()}</span>
                <span>{d._count.tickets} {t('nav.tickets').toLowerCase()}</span>
                <span>{d._count.qTasks} {t('nav.qTasks').toLowerCase()}</span>
              </div>
              {(d.viewSlaMinutes != null ||
                d.assignmentSlaMinutes != null ||
                d.viewPenaltyDays != null ||
                d.assignmentPenaltyDays != null) && (
                <p className="mt-3 border-t hairline pt-2 text-caption text-warning-fg dark:text-signal-600">
                  {t('departments.slaOverrides')}
                </p>
              )}
            </button>
          ))}
        </div>
      )}

    </>
  );
}

/**
 * What a department may set for itself, beside the company setting it falls back
 * to — the precedence `SlaSettingsService.resolve` applies.
 */
const SLA_OVERRIDES: Array<{
  field: string;
  setting: string;
  label: TranslationKey;
  kind: 'number' | 'days' | 'boolean';
}> = [
  { field: 'viewSlaMinutes', setting: 'sla.viewSlaMinutes', label: 'settings.viewSlaMinutes', kind: 'number' },
  { field: 'assignmentSlaMinutes', setting: 'sla.assignmentSlaMinutes', label: 'settings.assignmentSlaMinutes', kind: 'number' },
  { field: 'viewPenaltyDays', setting: 'sla.viewPenaltyDays', label: 'settings.viewPenaltyDays', kind: 'days' },
  { field: 'assignmentPenaltyDays', setting: 'sla.assignmentPenaltyDays', label: 'settings.assignmentPenaltyDays', kind: 'days' },
  { field: 'penaltiesAreCumulative', setting: 'sla.penaltiesAreCumulative', label: 'settings.penaltiesAreCumulative', kind: 'boolean' },
  { field: 'allowSelfAssignment', setting: 'sla.allowSelfAssignment', label: 'settings.allowSelfAssignment', kind: 'boolean' },
  { field: 'excludeOffShiftEmployees', setting: 'sla.excludeOffShiftEmployees', label: 'settings.excludeOffShift', kind: 'boolean' },
];

export function DepartmentDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  // A setting as the SLA tab shows it; null when there is none.
  const shown = (kind: 'number' | 'days' | 'boolean', value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    if (kind === 'boolean') return value ? t('common.yes') : t('common.no');
    return kind === 'days' ? formatDays(Number(value), locale) : formatNumber(Number(value), locale);
  };
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('members');

  const { data: department, isLoading, error, refetch } = useQuery({
    queryKey: ['department', id],
    queryFn: () => api.get<any>(`/departments/${id}`),
  });

  const { data: staff } = useQuery({
    queryKey: ['department', id, 'current-staff'],
    queryFn: () => api.get<any[]>(`/departments/${id}/current-staff`),
    enabled: tab === 'onshift',
    refetchInterval: 60_000,
  });

  const { data: summary } = useQuery({
    queryKey: ['department', id, 'summary'],
    queryFn: () => api.get<any>(`/tickets/department-summary/${id}`),
  });

  // The company defaults, so a field the department leaves alone still shows the
  // figure in force (operator, 2026-10-07). Any signed-in user may read them.
  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Record<string, Record<string, unknown>>>('/settings'),
    enabled: tab === 'sla',
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => api.del(`/departments/${id}/members/${userId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['department', id] }),
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div className="mb-4">
        <Link to="/departments" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('departments.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{department.code}</span>
            <span>{localized(department)}</span>
          </span>
        }
        subtitle={department.description}
        action={
          can(P.DEPARTMENTS_MANAGE) && (
            <>
              <Button onClick={() => navigate(`/departments/${id}/edit`)}>{t('common.edit')}</Button>
              <Button variant="primary" icon={<UserPlus className="h-4 w-4" />} onClick={() => navigate(`/departments/${id}/members/new`)}>
                {t('common.add')}
              </Button>
            </>
          )
        }
      />

      {summary && (
        <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card>
            <p className="text-caption uppercase text-[var(--text-tertiary)]">{t('common.unassigned')}</p>
            <p className="mt-1 text-metric tabular text-warning-fg dark:text-warning-dark">{summary.unassigned}</p>
          </Card>
          <Card>
            <p className="text-caption uppercase text-[var(--text-tertiary)]">{t('dash.urgent')}</p>
            <p className="mt-1 text-metric tabular text-error-fg dark:text-error-dark">{summary.urgent}</p>
          </Card>
          <Card>
            <p className="text-caption uppercase text-[var(--text-tertiary)]">{t('dash.viewBreaches')}</p>
            <p className="mt-1 text-metric tabular">{summary.viewBreaches}</p>
          </Card>
          <Card>
            <p className="text-caption uppercase text-[var(--text-tertiary)]">{t('dash.assignmentBreaches')}</p>
            <p className="mt-1 text-metric tabular">{summary.assignmentBreaches}</p>
          </Card>
        </div>
      )}

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'members', label: t('departments.members'), count: department.members.length },
          { id: 'onshift', label: t('shifts.onShiftNow') },
          { id: 'sla', label: t('departments.slaOverrides') },
        ]}
      />

      <div className="mt-5">
        {tab === 'members' && (
          <Card padded={false}>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('common.email')}</Th>
                  <Th>{t('common.role')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {department.members.map((m: any) => (
                  <Tr key={m.id}>
                    <Td>
                      <Link to={`/employees/${m.user.id}`} className="hover:text-primary-600 dark:hover:text-primary-400">
                        {localized(m.user)}
                      </Link>
                    </Td>
                    <Td className="text-muted" dir="ltr">{m.user.email}</Td>
                    <Td>
                      <Badge tone={m.memberType === 'LEADER' ? 'brand' : 'neutral'}>
                        {enumLabel(m.memberType, locale)}
                      </Badge>
                    </Td>
                    <Td>
                      <span className="flex items-center gap-2">
                        <StatusDot tone={m.user.isActive ? 'online' : 'offline'} />
                        <span className="text-muted">
                          {m.user.isActive ? t('employees.active') : t('employees.inactive')}
                        </span>
                      </span>
                    </Td>
                    <Td align="end">
                      {can(P.DEPARTMENTS_MANAGE) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<UserMinus className="h-4 w-4" />}
                          onClick={() => removeMember.mutate(m.user.id)}
                        >
                          {t('common.remove')}
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}

        {tab === 'onshift' && (
          <Card padded={false}>
            <Table>
              <thead>
                <tr>
                  <Th>{t('common.name')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('common.reason')}</Th>
                </tr>
              </thead>
              <tbody>
                {staff?.map((row) => (
                  <Tr key={row.user.id}>
                    <Td>{localized(row.user)}</Td>
                    <Td>
                      <span className="flex items-center gap-2">
                        <StatusDot tone={row.onShift ? 'online' : 'offline'} pulse={row.onShift} />
                        <span>{row.onShift ? enumLabel(row.shiftType, locale) : t('dash.notOnShift')}</span>
                      </span>
                    </Td>
                    <Td align="end" className="text-muted">{enumLabel(row.reason, locale)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}

        {tab === 'sla' &&
          (settings.isLoading ? (
            <LoadingBlock />
          ) : (
            <Card>
              <CardHeader title={t('departments.slaOverrides')} subtitle={t('departments.inheritNote')} />
              <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                {SLA_OVERRIDES.map((row) => (
                  <OverrideRow
                    key={row.field}
                    label={t(row.label)}
                    own={shown(row.kind, department[row.field])}
                    companyDefault={shown(row.kind, settings.data?.sla?.[row.setting])}
                  />
                ))}
              </dl>
            </Card>
          ))}
      </div>

    </>
  );
}

/**
 * One setting: the figure in force, and whether the department set it or the
 * company default applies. `own` is the department's, null when it inherits.
 */
function OverrideRow({ label, own, companyDefault }: { label: string; own: string | null; companyDefault: string | null }) {
  const { t } = useI18n();
  const inherited = own === null;
  return (
    <div className="flex items-baseline justify-between gap-4 border-b hairline py-2">
      <dt className="text-body-sm text-[var(--text-tertiary)]">{label}</dt>
      <dd className="flex flex-wrap items-baseline justify-end gap-x-2 gap-y-1">
        {inherited && companyDefault === null ? (
          // The company settings did not load: say where the figure comes from, at least.
          <span className="text-body-sm text-faint italic">{t('departments.inherits')}</span>
        ) : (
          <>
            <span className="text-body-sm tabular text-[var(--text-primary)]">{own ?? companyDefault}</span>
            {inherited ? (
              <Badge>{t('departments.companyDefault')}</Badge>
            ) : (
              <Badge
                tone="brand"
                title={companyDefault === null ? undefined : t('departments.companyDefaultIs', { value: companyDefault })}
              >
                {t('departments.override')}
              </Badge>
            )}
          </>
        )}
      </dd>
    </div>
  );
}
